/**
 * Deck.gl Icon Mapping Utilities
 *
 * Converts MapLibre sprite JSON to Deck.gl IconLayer iconMapping format.
 * Also provides sizing constants for meter-based props and pixel-based amenities.
 */

/**
 * Sprite metadata from MapLibre sprite JSON
 */
export interface SpriteMetadata {
  x: number;
  y: number;
  width: number;
  height: number;
  pixelRatio?: number;
}

/**
 * Deck.gl IconMapping entry
 */
export interface DeckIconMapping {
  x: number;
  y: number;
  width: number;
  height: number;
  anchorY: number;
  mask: boolean;
}

/**
 * Convert MapLibre sprite JSON to Deck.gl iconMapping format
 *
 * @param spriteJson - The sprite metadata from .json file
 * @returns Deck.gl compatible iconMapping with anchorY at bottom
 */
export function convertSpriteToDeckIconMapping(
  spriteJson: Record<string, SpriteMetadata>
): Record<string, DeckIconMapping> {
  const result: Record<string, DeckIconMapping> = {};
  for (const [name, meta] of Object.entries(spriteJson)) {
    result[name] = {
      x: meta.x,
      y: meta.y,
      width: meta.width,
      height: meta.height,
      anchorY: meta.height, // Anchor at bottom of icon
      mask: false,
    };
  }
  return result;
}

/**
 * Convert MapLibre sprite JSON to Deck.gl iconMapping with the anchor at
 * the icon's CENTER. Stickers (plan 120) lay flat on the ground via
 * `billboard: false` — bottom-anchor causes the icon to render north of the
 * placement coord, so the cursor appears to drop the sticker offset from
 * where it actually lands. Center-anchor keeps the cursor and the sticker's
 * visual center co-located.
 */
export function convertSpriteToDeckIconMappingCentered(
  spriteJson: Record<string, SpriteMetadata>
): Record<string, DeckIconMapping> {
  const result: Record<string, DeckIconMapping> = {};
  for (const [name, meta] of Object.entries(spriteJson)) {
    result[name] = {
      x: meta.x,
      y: meta.y,
      width: meta.width,
      height: meta.height,
      anchorY: meta.height / 2,
      mask: false,
    };
  }
  return result;
}

/**
 * Create a masked version of iconMapping for shadow silhouettes
 *
 * With mask: true, Deck.gl ignores texture colors and renders only the shape
 * using getColor. This creates true black silhouettes, not dark tinted images.
 *
 * @param mapping - Original iconMapping
 * @returns New iconMapping with mask: true for all entries
 */
export function createMaskedMapping(
  mapping: Record<string, DeckIconMapping>
): Record<string, DeckIconMapping> {
  const masked: Record<string, DeckIconMapping> = {};
  for (const [key, value] of Object.entries(mapping)) {
    masked[key] = { ...value, mask: true };
  }
  return masked;
}

/**
 * Prop meter sizes at z18 reference zoom
 * Props scale with map (sizeUnits: 'meters')
 * Values tuned visually - adjust after implementation
 */
export const PROP_METER_SIZES: Record<string, number> = {
  tree: 6,
  "tree-oak": 6,
  "tree-pine": 5,
  "tree-palm": 4,
  palm: 4,
  pine: 5,
  bush: 3,
  rock: 3,
  "rock-large": 4,
  "rock-small": 2,
  lamp: 2,
  fountain: 5,
};
export const DEFAULT_PROP_METER_SIZE = 3;

/**
 * Default shadow configuration (used when theme doesn't specify)
 * Offset direction matches existing building shadows (down-right)
 */
export const DEFAULT_SHADOW_OFFSET = 3; // pixels at z18
export const DEFAULT_SHADOW_OPACITY = 0.3; // 0-1

/**
 * Compute shadow offset scaled by zoom level
 * At z18 (reference), offset equals the config value
 * At lower zooms, offset is smaller; at higher zooms, larger
 * Uses exponential base 2 scaling (same as icon size)
 *
 * @param offsetX - Shadow offset X in pixels at z18 (positive = right)
 * @param offsetY - Shadow offset Y in pixels at z18 (positive = down)
 * @param zoom - Current zoom level
 * @returns [x, y] pixel offset scaled for current zoom
 */
export function computeShadowOffset(
  offsetX: number,
  offsetY: number,
  zoom: number
): [number, number] {
  // Damped scale: sqrt of full exponential for perceptual consistency
  // z16 = 0.5x, z17 = 0.71x, z18 = 1x, z19 = 1.41x, z20 = 2x
  const scale = Math.pow(2, (zoom - 18) * 0.5);
  return [offsetX * scale, offsetY * scale];
}

/**
 * Convert opacity (0-1) to RGBA alpha (0-255)
 * @param opacity - 0-1 opacity value
 * @returns RGBA color array [0, 0, 0, alpha]
 */
export function opacityToRgba(
  opacity: number
): [number, number, number, number] {
  const alpha = Math.round(opacity * 255);
  return [0, 0, 0, alpha];
}

/**
 * Density filtering thresholds for props
 * Props progressive reveal: z16: 10%, z17: 30%, z18: 60%, z19+: 100%
 */
export function getMaxDensityForZoom(zoom: number): number {
  if (zoom < 16) return 0;
  if (zoom < 17) return 0.1;
  if (zoom < 18) return 0.3;
  if (zoom < 19) return 0.6;
  return 1.0;
}
